import { Skeleton } from "@Light/components/page/Skeleton";

export type ExpressPaymentMethod = "apple_pay" | "google_pay";

/** "existing" keeps the payment method already on the account. */
export type PaymentMethod = "existing" | "card" | ExpressPaymentMethod;

export type PaymentOption = {
  value: PaymentMethod;
  label: string;
  icon: React.ReactNode;
  content?: React.ReactNode;
};

export type PaymentOptionsProps = {
  options: PaymentOption[];
  value?: PaymentMethod;
  onChange: (option: PaymentMethod) => void;
};

/**
 * The payment methods this browser can offer, each expanding to what it needs
 * to collect. A lone option needs no radio and stays expanded.
 */
export const PaymentOptions = ({
  options,
  value,
  onChange,
}: PaymentOptionsProps) => {
  const isOnlyOption = options.length === 1;
  return (
    <div className="flex flex-col gap-3">
      {options.map(({ value: method, label, icon, content }) => {
        const isSelected = isOnlyOption || method === value;
        return (
          <div
            key={method}
            className="rounded-xl border border-secondary bg-primary"
            data-testid={`payment-option-${method}`}
          >
            <label className="flex cursor-pointer items-center gap-4 p-4">
              {!isOnlyOption && (
                <input
                  type="radio"
                  name="payment-option"
                  checked={isSelected}
                  onChange={() => {
                    onChange(method);
                  }}
                  className={
                    isSelected
                      ? "size-4 shrink-0 appearance-none rounded-full border-[5px] border-brand-solid bg-primary ring-brand"
                      : "size-4 shrink-0 appearance-none rounded-full border border-primary bg-primary ring-brand"
                  }
                />
              )}
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <span className="flex w-6 shrink-0 items-center justify-center">
                  {icon}
                </span>
                <span className="text-sm font-medium leading-5 text-secondary">
                  {label}
                </span>
              </span>
            </label>
            {isSelected && content ? (
              <div className="px-4 pb-4">{content}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export const PaymentOptionsSkeleton = () => (
  <div className="flex flex-col gap-3">
    <Skeleton className="h-13 rounded-xl" />
    <Skeleton className="h-13 rounded-xl" />
  </div>
);
